import { useEffect, useMemo, useState } from "react";
import { TrackList } from "@/components/tracks/TrackList";
import { estimateStorage } from "@/lib/offline";
import { offlineTracks, removeAll, useOfflineJobs } from "@/lib/offlineQueue";
import { usePlayer } from "@/hooks/usePlayer";
import { sfx } from "@/lib/sfx";

function mb(n: number): string {
  return n >= 1e9 ? `${(n / 1e9).toFixed(1)} Go` : `${Math.round(n / 1e6)} Mo`;
}

/** Everything saved on this device — works with no network at all. */
export function OfflinePage() {
  const player = usePlayer();
  const { jobs, version } = useOfflineJobs();
  const tracks = useMemo(() => offlineTracks(), [version]); // eslint-disable-line react-hooks/exhaustive-deps
  const [space, setSpace] = useState<{ usage: number; quota: number } | null>(null);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => { estimateStorage().then(setSpace).catch(() => {}); }, [version]);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <h1 style={{ margin: 0 }}>Téléchargés</h1>
          <div className="muted" style={{ fontSize: 14, marginTop: 4 }}>
            {tracks.length} titre{tracks.length > 1 ? "s" : ""} sur cet appareil
            {space ? ` · ${mb(space.usage)} utilisés sur ${mb(space.quota)}` : ""}
            {!online && " · 📴 hors connexion"}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn primary" disabled={!tracks.length}
                  onClick={() => { sfx.pop(); void player.play(tracks, 0); }}>▶ Tout lire</button>
          {tracks.length > 0 && (
            <button className="btn" onClick={() => {
              if (!window.confirm(`Supprimer les ${tracks.length} titres téléchargés de cet appareil ?`)) return;
              void removeAll(tracks);
            }}>Tout supprimer</button>
          )}
        </div>
      </div>
      {jobs.map((j) => (
        <div key={j.key} className="dl-job">
          ⬇ {j.label} — {j.done + j.failed}/{j.ids.length}
          {j.failed > 0 && <span className="muted"> ({j.failed} échec{j.failed > 1 ? "s" : ""})</span>}
        </div>
      ))}
      {tracks.length === 0 ? (
        <p className="muted" style={{ marginTop: 24, maxWidth: 520 }}>
          Rien pour l'instant. Sur une playlist, un album, tes titres likés ou un mix, appuie sur
          <b> ⬇ Télécharger</b> : les sons seront lisibles ici même sans réseau (métro, avion…).
        </p>
      ) : (
        <div style={{ marginTop: 18 }}><TrackList tracks={tracks} /></div>
      )}
    </div>
  );
}
